import { useState } from 'react';
import { useQuery } from 'react-query';
import {
  Alert, Box, Button, CircularProgress, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, Tooltip, Typography,
} from '@mui/material';
import axios from 'libs/Axios';

type HistoryItem = {
  operation_id: string;
  authorised_by: string;
  authorised_by_name?: string;
  source_layer_name?: string;
  destination_layer_names?: string[];
  requested_at: string;
  completed_at: string | null;
  status: string;
  error_message: string | null;
};
type HistoryPage = { data: HistoryItem[]; page: number; size: number; totalElements: number; totalPages: number };

const statusLabels: Record<string, string> = {
  IN_QUEUE: 'Queued', IN_PROGRESS: 'Generating', COMPLETED: 'Completed', FAILED: 'Failed',
};

export function BudgetGenerationHistory({ cubeId }: { cubeId: string }) {
  const [page, setPage] = useState(0);
  const size = 10;
  const history = useQuery(['budget-control-history', cubeId, page], async () =>
    (await axios.get<HistoryPage>(`/api/v1/tenant/cube/${cubeId}/budget-control/history`, { params: { page, size } })).data, {
    keepPreviousData: true,
    refetchInterval: data => data?.data.some(item => ['IN_QUEUE', 'IN_PROGRESS'].includes(item.status)) ? 2000 : false,
  });

  if (history.isLoading) return <Stack alignItems='center' py={4}><CircularProgress size={24} aria-label='Loading generation history' /></Stack>;
  if (history.isError) return <Alert severity='error' action={<Button color='inherit' size='small' onClick={() => history.refetch()}>Retry</Button>}>
    Could not load generation history.
  </Alert>;
  if (!history.data?.totalElements) return <Typography variant='body2' color='text.secondary' sx={{ py: 3 }}>
    No generation history yet.
  </Typography>;

  return <Box>
    <TableContainer sx={{ border: '1px solid #E3EBF6', borderRadius: 1 }}>
      <Table size='small' aria-label='Budget generation history' sx={{ minWidth: 850 }}>
        <TableHead sx={{ bgcolor: '#FBFBFB' }}>
          <TableRow>
            {['Authorised by', 'Source layer', 'Destination layers', 'Requested at', 'Completed at', 'Status'].map(label =>
              <TableCell key={label} sx={{ color: '#3B4797', fontWeight: 700 }}>{label}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {history.data.data.map(item => <TableRow key={item.operation_id}>
            <TableCell sx={{ maxWidth: 180, overflowWrap: 'anywhere' }}>
              {item.authorised_by_name && item.authorised_by_name !== item.authorised_by ? item.authorised_by_name : 'Unknown user'}
            </TableCell>
            <TableCell sx={{ overflowWrap: 'anywhere' }}>{item.source_layer_name || 'Unavailable layer'}</TableCell>
            <TableCell sx={{ overflowWrap: 'anywhere' }}>
              {item.destination_layer_names?.length ? item.destination_layer_names.map((name, index) =>
                <Typography key={index} variant='body2'>{name}</Typography>) : '—'}
            </TableCell>
            <TableCell>{new Date(item.requested_at).toLocaleString()}</TableCell>
            <TableCell>{item.completed_at ? new Date(item.completed_at).toLocaleString() : '—'}</TableCell>
            <TableCell>
              <Tooltip title={item.error_message || ''}>
                <Typography variant='body2' color={item.status === 'FAILED' ? 'error' : 'text.secondary'}>
                  {statusLabels[item.status] || item.status}
                </Typography>
              </Tooltip>
            </TableCell>
          </TableRow>)}
        </TableBody>
      </Table>
    </TableContainer>
    <TablePagination
      component='div'
      count={history.data.totalElements}
      page={page}
      rowsPerPage={size}
      rowsPerPageOptions={[size]}
      onPageChange={(_event, nextPage) => setPage(nextPage)}
      backIconButtonProps={{ disabled: history.isFetching || page === 0 }}
      nextIconButtonProps={{ disabled: history.isFetching || page + 1 >= history.data.totalPages }}
      sx={{ '& .MuiTablePagination-toolbar': { px: 0 } }}
    />
  </Box>;
}
